import { BlockNoteEditor, type PartialBlock } from '@blocknote/core';

import { couplespaceTableEnterExtension } from './couplespace-table-enter.web';
import { couplespaceSchema } from './editor-schema.web';
import {
  ensureFileIdsInBlocks,
  imageBlockHasFileReference,
  stripFileUrlsForStorage,
} from './file-block-persist';

const INLINE_CONTENT_TYPES = new Set(['text', 'link']);
const TABLE_NODE_TYPES = new Set(['tableCell', 'tableRow', 'tableParagraph', 'tableHeader']);

const EDITOR_CREATE_OPTIONS = {
  schema: couplespaceSchema,
  extensions: [couplespaceTableEnterExtension],
};

function isTableContent(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'tableContent'
  );
}

function deepContainsTableNode(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some(deepContainsTableNode);
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.type === 'string') {
    if (obj.type === 'tableContent' || TABLE_NODE_TYPES.has(obj.type)) {
      return true;
    }
  }
  return Object.values(obj).some(deepContainsTableNode);
}

function extractTableContent(value: unknown): Record<string, unknown> | null {
  if (value === null || value === undefined || typeof value !== 'object') {
    return null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = extractTableContent(item);
      if (found) {
        return found;
      }
    }
    return null;
  }
  const obj = value as Record<string, unknown>;
  if (obj.type === 'tableContent') {
    return obj;
  }
  for (const nested of Object.values(obj)) {
    const found = extractTableContent(nested);
    if (found) {
      return found;
    }
  }
  return null;
}

function emptyParagraph(id?: string): PartialBlock {
  return { id, type: 'paragraph', content: '' };
}

type InlineTextNode = {
  type: string;
  text?: string;
  href?: string;
  content?: InlineTextNode[];
};

function linkDisplayText(node: InlineTextNode): string {
  if (node.type !== 'link' || !Array.isArray(node.content)) {
    return '';
  }
  return node.content.map((part) => part.text ?? '').join('');
}

/** Drop duplicate plain text / orphan suffixes left by partial markdown link parsing. */
export function fixInlineLinkArtifacts(content: PartialBlock['content']): PartialBlock['content'] {
  if (!Array.isArray(content)) {
    return content;
  }

  const nodes = content as InlineTextNode[];
  const withoutDuplicatePlain: InlineTextNode[] = [];
  for (const node of nodes) {
    const prev = withoutDuplicatePlain[withoutDuplicatePlain.length - 1];
    if (
      prev?.type === 'text' &&
      node.type === 'link' &&
      typeof prev.text === 'string' &&
      prev.text === linkDisplayText(node)
    ) {
      continue;
    }
    withoutDuplicatePlain.push(node);
  }

  const fixed: InlineTextNode[] = [];
  for (let i = 0; i < withoutDuplicatePlain.length; i++) {
    const current = withoutDuplicatePlain[i]!;
    const next = withoutDuplicatePlain[i + 1];
    if (
      current.type === 'link' &&
      next?.type === 'text' &&
      typeof next.text === 'string' &&
      next.text.length > 0
    ) {
      const anchorText = linkDisplayText(current);
      if (anchorText.endsWith(next.text) && anchorText !== next.text) {
        i += 1;
      }
    }
    fixed.push(current);
  }

  return fixed as PartialBlock['content'];
}

function sanitizeInlineContent(content: unknown): PartialBlock['content'] {
  if (content === undefined || content === null) {
    return undefined;
  }
  if (typeof content === 'string') {
    return content;
  }
  if (isTableContent(content) || deepContainsTableNode(content)) {
    return undefined;
  }
  if (!Array.isArray(content)) {
    return undefined;
  }
  const safe = content.filter(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      'type' in item &&
      INLINE_CONTENT_TYPES.has(String(item.type)),
  );
  return fixInlineLinkArtifacts(safe as PartialBlock['content']);
}

function sanitizeBlock(block: PartialBlock): PartialBlock {
  const embeddedTable =
    extractTableContent(block.content) ??
    (block.children?.length ? extractTableContent(block.children) : null);

  if (block.type === 'table') {
    if (isTableContent(block.content)) {
      return { ...block, children: [] };
    }
    if (embeddedTable) {
      return {
        id: block.id,
        type: 'table',
        props: block.props ?? {},
        content: embeddedTable,
        children: [],
      } as unknown as PartialBlock;
    }
    return emptyParagraph(block.id);
  }

  if (isTableContent(block.content) || embeddedTable) {
    const tableContent = isTableContent(block.content) ? block.content : embeddedTable!;
    return {
      id: block.id,
      type: 'table',
      props: block.props ?? {},
      content: tableContent,
      children: [],
    } as PartialBlock;
  }

  const children = (block.children ?? [])
    .map(sanitizeBlock)
    .filter((child) => child.type !== undefined);

  if (deepContainsTableNode(block.content)) {
    return emptyParagraph(block.id);
  }

  const safeChildren = deepContainsTableNode(block.children)
    ? children.filter((child) => !deepContainsTableNode(child))
    : children;

  return {
    ...block,
    content: sanitizeInlineContent(block.content),
    children: safeChildren,
  } as PartialBlock;
}

export function sanitizePartialBlocks(blocks: PartialBlock[]): PartialBlock[] {
  return blocks.map(sanitizeBlock);
}

function canCreateEditor(blocks: PartialBlock[]): boolean {
  if (blocks.length === 0) {
    return false;
  }
  try {
    BlockNoteEditor.create({
      ...EDITOR_CREATE_OPTIONS,
      initialContent: blocks,
    });
    return true;
  } catch {
    return false;
  }
}

function ensureValidPartialBlocks(blocks: PartialBlock[]): PartialBlock[] {
  const sanitized = sanitizePartialBlocks(blocks);
  if (canCreateEditor(sanitized)) {
    return sanitized;
  }

  let current = sanitized;
  for (let i = 0; i < current.length; i++) {
    if (imageBlockHasFileReference(current[i]!)) {
      continue;
    }
    const attempt = current.map((block, index) =>
      index === i ? emptyParagraph(block.id) : block,
    );
    if (canCreateEditor(attempt)) {
      current = attempt;
    }
  }

  if (canCreateEditor(current)) {
    return current;
  }

  const fallback = [{ type: 'paragraph', content: '' }] as PartialBlock[];
  return canCreateEditor(fallback) ? fallback : fallback;
}

export type RepairedBlocks = {
  blocks: PartialBlock[];
  repaired: boolean;
};

export function repairPartialBlocks(blocks: PartialBlock[]): RepairedBlocks {
  if (blocks.length === 0) {
    return {
      blocks: ensureValidPartialBlocks(blocks),
      repaired: false,
    };
  }

  const safe = ensureValidPartialBlocks(blocks);
  const wasCorrupt = !canCreateEditor(blocks);
  return {
    blocks: safe,
    repaired: wasCorrupt,
  };
}

export function validateBlocksForPersist(blocks: PartialBlock[]): PartialBlock[] | null {
  const sanitized = sanitizePartialBlocks(blocks);
  const withFileIds = ensureFileIdsInBlocks(sanitized);
  if (!canCreateEditor(withFileIds)) {
    return null;
  }
  return stripFileUrlsForStorage(withFileIds);
}

export function prepareBlocksForPersist(blocks: PartialBlock[]): PartialBlock[] | null {
  const { blocks: safe } = repairPartialBlocks(blocks);
  return validateBlocksForPersist(safe);
}

export function blocksContentEqual(a: PartialBlock[], b: PartialBlock[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
